import type { GameForSelect } from '~stores/games';
import {
    ALL_PROVINCES,
    ApplicationTargetEnum,
    ClientProvinceEnum,
} from '~types/app-selection';

/**
 * Which contests may be linked to a loyalty club.
 *
 * `validateExternalGame` in ctd-web-api is what actually accepts or rejects the
 * link, and it is deliberately permissive: linking is an editorial decision and
 * the mobile read path decides per viewer what renders. This filter is a
 * deliberate superset of the province half of that rule, not a mirror to hold in
 * manual lockstep: the API stays authoritative, so this widens rather than
 * tightens.
 *
 * It deliberately does **not** reuse Featured's
 * `isEligibleProvinceSetByAppTarget`: Featured's CA rule requires the game to
 * cover the full 12-province Circle K set exactly, while this rule only requires
 * one province outside Quebec. Composing Featured's rule for an "All" club
 * (QC-eligible AND CA-eligible) is unsatisfiable, since a set of exactly the 12
 * non-QC provinces cannot also contain QC, so composing them filters out every
 * option.
 */

// Derived rather than whitelisted so QC cannot enter the set: the CA rule's
// correctness rests on its absence. Same shape as the API's NON_QC_PROVINCES.
const NON_QC_PROVINCE_CODES = ALL_PROVINCES.filter(
    (province) => province !== ClientProvinceEnum.QC
).map((province) => province.toUpperCase());

function toProvinceCodeSet(game: GameForSelect): Set<string> {
    const codes = (game.provinces ?? [])
        .map((entry) => entry.province?.name)
        .filter((name): name is string => typeof name === 'string')
        .map((name) => name.trim().toUpperCase());
    return new Set(codes);
}

/** QC app needs Quebec; CA app needs at least one province outside Quebec. */
function isInScopeForAppTarget(
    provinceCodes: Set<string>,
    appTarget: ApplicationTargetEnum
): boolean {
    if (provinceCodes.size === 0) {
        return false;
    }
    if (appTarget === ApplicationTargetEnum.QC) {
        return provinceCodes.has(ClientProvinceEnum.QC);
    }
    return NON_QC_PROVINCE_CODES.some((code) => provinceCodes.has(code));
}

const BOTH_APP_TARGETS = [
    ApplicationTargetEnum.QC,
    ApplicationTargetEnum.CA,
] as const;

/**
 * The app targets a club is served to. `'All'` serves both, and an unrecognized
 * value widens to both deliberately: the API write check is the real gate, so
 * offering too much costs a rejected save with a legible message, while offering
 * too little empties the picker, which is the bug this rule exists to fix.
 */
function viewerAppTargets(
    clubAppTarget: string | undefined
): readonly ApplicationTargetEnum[] {
    if (clubAppTarget === ApplicationTargetEnum.QC) {
        return [ApplicationTargetEnum.QC];
    }
    if (clubAppTarget === ApplicationTargetEnum.CA) {
        return [ApplicationTargetEnum.CA];
    }
    return BOTH_APP_TARGETS;
}

/**
 * Offer any contest at least one viewer of the club could see, matching what the
 * API accepts. An "All" club serves both apps, so a Quebec-only or an
 * Ontario-only contest is a legitimate choice there: each viewer sees it or does
 * not, decided by the mobile read path. Content type is not filtered either;
 * linking a Labatt or age-verification contest is allowed even though mobile
 * hides it, enforced in `isGameLinkableToClub`
 * (api-mobile/loyalty-clubs/helpers/related-game.helper.ts). The only thing left
 * out is a contest no viewer of this club could ever see, which would save and
 * then render for nobody.
 */
export function isGameLinkableToLoyaltyClub(
    game: GameForSelect,
    clubAppTarget: string | undefined
): boolean {
    const provinceCodes = toProvinceCodeSet(game);

    return viewerAppTargets(clubAppTarget).some((target) =>
        isInScopeForAppTarget(provinceCodes, target)
    );
}
